import { useApiQuery } from "../../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../../common/infrastructure/routes/endpointsMetadata";
import { IStudioLinkPage } from "../models/studioLinkPageModels";
import { IStudioLinkPageItemFormModel } from "../models/studioLinkPageItemFormModel";

export const useStudioLinkPage = (studioId: number) => {
  const pageKey = [endpointsMetadata.studioLinkPage.getPage, studioId];

  const {
    data: page,
    isLoading,
    isFetching,
  } = useApiQuery<IStudioLinkPage>({
    queryKey: pageKey,
    endpoint: `${endpointsMetadata.studioLinkPage.getPage}?studioId=${studioId}`,
    enabled: studioId > 0,
    globalIsLoading: false,
  });

  const { mutate: mutateSave, isLoading: isSaving } = useApiMutation();
  const { mutate: mutateDelete, isLoading: isDeleting } = useApiMutation();
  const { mutate: mutateReorder, isLoading: isReordering } = useApiMutation();

  const saveItem = (
    values: IStudioLinkPageItemFormModel,
    onDone?: () => void,
  ) => {
    const isEdit = !!values.id && values.id > 0;

    mutateSave(
      {
        endpoint: isEdit
          ? endpointsMetadata.studioLinkPage.updateItem
          : endpointsMetadata.studioLinkPage.addItem,
        method: isEdit ? "put" : "post",
        queryParams: { studioId },
        payload: {
          id: values.id ?? 0,
          name: values.name,
          url: values.url,
          // The API stores a blank icon as "no icon" — the form has no null to send.
          iconUrl: values.iconUrl?.trim() || null,
        },
        invalidateKeys: [pageKey],
        successMessage: isEdit
          ? "Линкът беше запазен успешно."
          : "Линкът беше добавен успешно.",
      },
      { onSuccess: () => onDone?.() },
    );
  };

  const deleteItem = (id: number) => {
    mutateDelete({
      endpoint: endpointsMetadata.studioLinkPage.deleteItem,
      method: "delete",
      queryParams: { studioId, itemId: id },
      invalidateKeys: [pageKey],
      successMessage: "Линкът беше премахнат успешно.",
    });
  };

  const reorderItems = (orderedIds: number[]) =>
    new Promise<void>((resolve, reject) => {
      mutateReorder(
        {
          endpoint: endpointsMetadata.studioLinkPage.reorder,
          method: "post",
          queryParams: { studioId },
          payload: { orderedIds },
          invalidateKeys: [pageKey],
          successMessage: "Подредбата беше запазена успешно.",
        },
        { onSuccess: () => resolve(), onError: () => reject() },
      );
    });

  return {
    page,
    items: page?.items ?? [],
    publicUrl: page?.publicUrl ?? "",
    isLoading: isLoading || isFetching,
    isSaving,
    isDeleting,
    isReordering,
    saveItem,
    deleteItem,
    reorderItems,
  };
};
